// @flow
import { of } from 'rxjs';
import { catchError, mergeMap, map } from 'rxjs/operators';
import update from 'immutability-helper';
import { endpoints, getPayoutsApi, postPayoutApi, deletePayoutApi, postPayoutPayPalApi } from './api';
import { setNewAlert } from '../../alert/duck';
import { ofType } from 'redux-observable';

import type { Observable } from 'rxjs';
import type { DisbursementBankRequest } from './api';
import type { HTTPError } from '../../../error';
import type { Payout, Country } from '../../../entities';

export type PayoutsAction = {
  +type: string;
  +payout?: Payout;
  +payouts?: Payout[];
  +email?: string;
  +country?: string;
  +error?: HTTPError;
};

export type PayoutsState = {
  +isFetchingPayouts?: boolean;
  +isPostingPayout?: boolean;
  +isDeletingPayout?: boolean;
  +payouts?: Payout[];
  +successDelete?: boolean;
  +fetchPayoutsError?: HTTPError;
  +postPayoutError?: HTTPError;
  +deletePayoutError?: HTTPError;
};

// Actions
const PAYOUT_FETCH_PAYOUTS_REQUEST = 'PAYOUT_FETCH_PAYOUTS_REQUEST';
const PAYOUT_FETCH_PAYOUTS_SUCCESS = 'PAYOUT_FETCH_PAYOUTS_SUCCESS';
const PAYOUT_FETCH_PAYOUTS_ERROR = 'PAYOUT_FETCH_PAYOUTS_ERROR';
const PAYOUT_POST_PAYOUT_REQUEST = 'PAYOUT_POST_PAYOUT_REQUEST';
const PAYOUT_POST_PAYOUT_SUCCESS = 'PAYOUT_POST_PAYOUT_SUCCESS';
const PAYOUT_POST_PAYOUT_ERROR = 'PAYOUT_POST_PAYOUT_ERROR';
const PAYOUT_POST_PAYOUT_PAY_PAL_REQUEST = 'PAYOUT_POST_PAYOUT_PAY_PAL_REQUEST';
const PAYOUT_POST_PAYOUT_PAY_PAL_SUCCESS = 'PAYOUT_POST_PAYOUT_PAY_PAL_SUCCESS';
const PAYOUT_POST_PAYOUT_PAY_PAL_ERROR = 'PAYOUT_POST_PAYOUT_PAY_PAL_ERROR';
const PAYOUT_DELETE_PAYOUT_REQUEST = 'PAYOUT_DELETE_PAYOUT_REQUEST';
const PAYOUT_DELETE_PAYOUT_SUCCESS = 'PAYOUT_DELETE_PAYOUT_SUCCESS';
const PAYOUT_DELETE_PAYOUT_ERROR = 'PAYOUT_DELETE_PAYOUT_ERROR';

// Action creators
export function fetchPayouts(): PayoutsAction {
  return {
    type: PAYOUT_FETCH_PAYOUTS_REQUEST,
  }
}

export function fetchPayoutsSuccess(payouts: Payout[]): PayoutsAction {
  return {
    type: PAYOUT_FETCH_PAYOUTS_SUCCESS,
    payouts,
  }
}

export function fetchPayoutsErrorFn(error: HTTPError): PayoutsAction {
  return {
    type: PAYOUT_FETCH_PAYOUTS_ERROR,
    error,
  }
}

export function postPayout(payout: Payout): PayoutsAction {
  return {
    type: PAYOUT_POST_PAYOUT_REQUEST,
    payout,
  }
}

export function postPayoutSuccess(payout: Payout): PayoutsAction {
  return {
    type: PAYOUT_POST_PAYOUT_SUCCESS,
    payout,
  }
}

export function postPayoutErrorFn(error: HTTPError): PayoutsAction {
  return {
    type: PAYOUT_POST_PAYOUT_ERROR,
    error,
  }
}

export function postPayoutPayPal(email: string, country: string): PayoutsAction {
  return {
    type: PAYOUT_POST_PAYOUT_PAY_PAL_REQUEST,
    email,
    country,
  }
}

export function postPayoutPayPalSuccess(payout: Payout): PayoutsAction {
  return {
    type: PAYOUT_POST_PAYOUT_PAY_PAL_SUCCESS,
    payout,
  }
}

export function postPayoutPayPalErrorFn(error: HTTPError): PayoutsAction {
  return {
    type: PAYOUT_POST_PAYOUT_PAY_PAL_ERROR,
    error,
  }
}


export function deletePayout(): PayoutsAction {
  return {
    type: PAYOUT_DELETE_PAYOUT_REQUEST,
  }
}

export function deletePayoutSuccess(): PayoutsAction {
  return {
    type: PAYOUT_DELETE_PAYOUT_SUCCESS,
  }
}

export function deletePayoutErrorFn(error: HTTPError): PayoutsAction {
  return {
    type: PAYOUT_DELETE_PAYOUT_ERROR,
    error,
  }
}

export const actions = {
  PAYOUT_FETCH_PAYOUTS_REQUEST,
  PAYOUT_FETCH_PAYOUTS_SUCCESS,
  PAYOUT_FETCH_PAYOUTS_ERROR,
  PAYOUT_POST_PAYOUT_REQUEST,
  PAYOUT_POST_PAYOUT_SUCCESS,
  PAYOUT_POST_PAYOUT_ERROR,
  PAYOUT_POST_PAYOUT_PAY_PAL_REQUEST,
  PAYOUT_POST_PAYOUT_PAY_PAL_SUCCESS,
  PAYOUT_POST_PAYOUT_PAY_PAL_ERROR,
  PAYOUT_DELETE_PAYOUT_REQUEST,
  PAYOUT_DELETE_PAYOUT_SUCCESS,
  PAYOUT_DELETE_PAYOUT_ERROR,
  fetchPayouts,
  fetchPayoutsSuccess,
  fetchPayoutsErrorFn,
  postPayout,
  postPayoutSuccess,
  postPayoutErrorFn,
  postPayoutPayPal,
  postPayoutPayPalSuccess,
  postPayoutPayPalErrorFn,
  deletePayout,
  deletePayoutSuccess,
  deletePayoutErrorFn,
};

export const initialState: PayoutsState = {};

//Reducer
export default function reducer(state: PayoutsState = initialState, action: PayoutsAction): PayoutsState {
  switch (action.type) {
    case PAYOUT_FETCH_PAYOUTS_REQUEST:
      return update(state, {
        isFetchingPayouts: { $set: true },
      });
    case PAYOUT_FETCH_PAYOUTS_SUCCESS:
      return update(state, {
        $unset: ['isFetchingPayouts'],
        payouts: { $set: action.payouts },
      });
    case PAYOUT_FETCH_PAYOUTS_ERROR:
      return update(state, {
        $unset: ['isFetchingPayouts'],
        fetchPayoutsError: { $set: action.error },
      });
    case PAYOUT_POST_PAYOUT_REQUEST:
    case PAYOUT_POST_PAYOUT_PAY_PAL_REQUEST:
      return update(state, {
        isPostingPayout: { $set: true },
      });
    case PAYOUT_POST_PAYOUT_SUCCESS:
    case PAYOUT_POST_PAYOUT_PAY_PAL_SUCCESS:
      return update(state, {
        $unset: ['isPostingPayout'],
        payouts: { $set: action.payout },
      });
    case PAYOUT_POST_PAYOUT_ERROR:
    case PAYOUT_POST_PAYOUT_PAY_PAL_ERROR:
      return update(state, {
        $unset: ['isPostingPayout'],
        postPayoutError: { $set: action.error },
      });
    case PAYOUT_DELETE_PAYOUT_REQUEST:
      return update(state, {
        $unset: ['deletePayoutError', 'successDelete'],
        isDeletingPayout: { $set: true },
      });
    case PAYOUT_DELETE_PAYOUT_SUCCESS:
      if (!state.payouts) return state;
      return update(state, {
        $unset: ['isDeletingPayout', 'payouts'],
        successDelete: { $set: true },
      });
    case PAYOUT_DELETE_PAYOUT_ERROR:
      return update(state, {
        $unset: ['isDeletingPayout'],
        deletePayoutError: { $set: action.error },
      });
    default:
      return state;
  }
}

// Epics
export function fetchPayoutsEpic(action$: Observable<PayoutsAction>): Observable<PayoutsAction> {
  return action$.pipe(
    ofType(PAYOUT_FETCH_PAYOUTS_REQUEST),
    mergeMap(() => getPayoutsApi().pipe(
      map((payout: Payout) => fetchPayoutsSuccess(payout)),
      catchError((e: HTTPError) => of(fetchPayoutsErrorFn(e))))
    ));
}

export function postPayoutEpic(action$: Observable<PayoutsAction>): Observable<PayoutsAction> {
  return action$.pipe(
    ofType(PAYOUT_POST_PAYOUT_REQUEST),
    mergeMap(({ payout }) => {
      const request: DisbursementBankRequest = {
        bankId: payout.bankId,
        specialCode: payout.accountNumberCci || payout.routingNumber,
        accountNumber: payout.accountNumber,
        billingAddress: {
          firstName: payout.billingAddress.firstName,
          idNumber: payout.billingAddress.idNumber || 'S/N', // NOTE: The US form design. It does not have id Number
          lastName: payout.billingAddress.lastName,
          phone: payout.billingAddress.phone,
          address1: `${payout.billingAddress.address1}. ${payout.billingAddress.address2}`,
          country: payout.billingAddress.country,
          admin1: parseInt(payout.billingAddress.admin1),
          admin2: parseInt(payout.billingAddress.admin2),
          zipCode: payout.billingAddress.zipCode,
        }
      };

      if (payout.country === 'PE') {
        request.billingAddress.admin3 = parseInt(payout.billingAddress.admin3);
      }

      return postPayoutApi(request).pipe(
        map((payout: Payout) => postPayoutSuccess(payout)),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postPayoutErrorFn(e),
              setNewAlert('preferences', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (e.code === 400) {
            return of(postPayoutErrorFn(e),
              setNewAlert('preferences', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postPayoutErrorFn(e));
        }));
    }));
}

export function postPayoutPayPalEpic(action$: Observable<PayoutsAction>): Observable<PayoutsAction> {
  return action$.pipe(
    ofType(PAYOUT_POST_PAYOUT_PAY_PAL_REQUEST),
    mergeMap(({ email, country }) => postPayoutPayPalApi({ email, country }).pipe(
      map((payout: Payout) => postPayoutPayPalSuccess(payout)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(postPayoutErrorFn(e),
            setNewAlert('preferences', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(postPayoutErrorFn(e),
            setNewAlert('preferences', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(postPayoutErrorFn(e));
      }))
    ));
}

export function deletePayoutEpic(action$: Observable<PayoutsAction>): Observable<PayoutsAction> {
  return action$.pipe(
    ofType(PAYOUT_DELETE_PAYOUT_REQUEST),
    mergeMap(() => deletePayoutApi().pipe(
      map(() => deletePayoutSuccess()),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(deletePayoutErrorFn(e),
            setNewAlert('preferences', 'error', undefined, 'httpErrors.post.500'),
          );
        } else if (e.code === 400) {
          return of(deletePayoutErrorFn(e),
            setNewAlert('preferences', 'error', undefined, 'httpErrors.post.400'),
          );
        }
        return of(deletePayoutErrorFn(e));
      }))
    ));
}





// WEBPACK FOOTER //
// ./src/modules/preferences/payouts/duck.js